import { useState, type ReactNode } from "react";

import { usePeriods, type PeriodGranularity, type PeriodRow } from "../../api/hooks.ts";
import { localToday } from "../../domain/breakdown.ts";
import { isoDate } from "../../domain/calendar.ts";
import {
  formatDay,
  formatMonth,
  formatPercent,
  formatR,
  formatSignedPips,
  toneOf,
} from "../../domain/format.ts";
import { Icon } from "../../ui/Icon.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";

/**
 * Los pips que gana y pierde POR SEMANA y POR MES, con el R al lado siempre
 * (ADR 0007). Es lo que el propietario pidio con estas palabras: «ver cada
 * semana si voy bien o mal y ver cada mes como voy».
 *
 * El periodo en curso va RESALTADO arriba y la tabla debajo, del mas reciente
 * al mas antiguo. El conmutador es el `.seg` de la plantilla.
 *
 * Y lo que no se puede callar: un pip de GAIN 400 no es un pip de V75. Cuando
 * alguna fila suma pips de varios instrumentos, la respuesta lo dice en `note`
 * y aqui SE PINTA, con el mismo mecanismo que las notas de los desgloses de
 * coleccion. Esconderlo seria mentir con un numero. El R se queda al lado como
 * la medida comparable.
 */

const GRANULARITY_KEY = "periods:granularity";

/** Cuantos periodos se listan: las doce ultimas semanas o los doce ultimos meses. */
const ROWS_SHOWN = 12;

const OPTIONS: readonly { value: PeriodGranularity; label: string }[] = [
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
];

export function PeriodsPanel(props: {
  /** El `from` del periodo elegido en Metricas; sin el, todo el historial. */
  from?: string | undefined;
  /** La zona del SERVIDOR (SPEC 19.19): decide que semana o mes es «este». */
  timeZone: string | undefined;
  className?: string | undefined;
}): ReactNode {
  const [granularity, setGranularity] = useState<PeriodGranularity>(readGranularity);
  const periods = usePeriods(granularity, props.from);

  const today = props.timeZone === undefined ? null : localToday(props.timeZone);
  const todayIso = today === null ? null : isoDate(today.year, today.month, today.day);

  const rows = periods.data?.rows ?? [];
  const note = periods.data?.note ?? null;

  /* El periodo en curso es la fila cuyo rango contiene hoy. Si no hay fila, es
     que no hay ninguna cerrada en el: se dice, no se pinta un cero. */
  const current =
    todayIso === null
      ? undefined
      : rows.find((row) => row.from <= todayIso && todayIso <= row.to);

  const recent = [...rows].reverse().slice(0, ROWS_SHOWN);

  function change(next: PeriodGranularity): void {
    setGranularity(next);
    try {
      window.localStorage.setItem(GRANULARITY_KEY, next);
    } catch {
      // Sin sitio donde guardar: la eleccion dura lo que dure la pestana.
    }
  }

  const noun = granularity === "week" ? "semana" : "mes";

  return (
    <section className={`panel ${props.className ?? ""}`}>
      <div className="panel-h">
        <div>
          <h3>Pips y R por {noun}</h3>
          <p>Lo que ganas y pierdes, {granularity === "week" ? "semana a semana" : "mes a mes"}</p>
        </div>
        <div className="seg" role="group" aria-label="Periodo">
          {OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={granularity === option.value ? "active" : ""}
              aria-pressed={granularity === option.value}
              onClick={() => change(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="panel-b">
        <StatusBlock
          loading={periods.isPending}
          error={periods.error}
          empty={rows.length === 0}
          emptyIcon="chart"
          emptyTitle={`Sin operaciones cerradas por ${noun}.`}
          emptyText="Cierra la primera y aqui empiezan a sumar los pips."
          onRetry={() => void periods.refetch()}
        >
          {note !== null && (
            <p className="infobox">
              <b>Un pip de un instrumento no es un pip de otro.</b> {note} El R de al lado es la
              cifra que si se puede comparar.
            </p>
          )}

          <div className="period-now">
            <small>
              {current === undefined
                ? `${granularity === "week" ? "Esta semana" : "Este mes"} · sin operaciones cerradas todavia`
                : `${granularity === "week" ? "Esta semana" : "Este mes"} · ${labelPeriod(current.key, granularity)}`}
            </small>
            <b className={`pips ${current === undefined ? "muted" : toneOf(current.totalPips)}`}>
              {current === undefined ? "—" : `${formatSignedPips(current.totalPips)} pips`}
            </b>
            <b className={`r ${current === undefined ? "muted" : toneOf(current.totalR)}`}>
              {current === undefined ? "—" : formatR(current.totalR)}
            </b>
            {current !== undefined && (
              <span className="meta">
                {current.trades} {current.trades === 1 ? "operacion" : "operaciones"} ·{" "}
                {current.instruments} {current.instruments === 1 ? "instrumento" : "instrumentos"}
                {" · "}acierto {formatPercent(current.winRate)}
              </span>
            )}
          </div>

          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{granularity === "week" ? "Semana" : "Mes"}</th>
                  <th scope="col">Operaciones</th>
                  <th scope="col">Pips</th>
                  <th scope="col">R</th>
                  <th scope="col">Expectativa</th>
                  <th scope="col">Acierto</th>
                  <th scope="col">Instrumentos</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row) => (
                  <tr key={row.key} className={row.key === current?.key ? "now" : undefined}>
                    <th scope="row" style={{ textAlign: "left", fontWeight: 600 }}>
                      {labelPeriod(row.key, granularity)}
                      <span className="hint" style={{ display: "block", fontWeight: 400 }}>
                        {formatDay(row.from)} – {formatDay(row.to)}
                      </span>
                    </th>
                    <td>{row.trades}</td>
                    <td className={toneOf(row.totalPips)}>{formatSignedPips(row.totalPips)}</td>
                    <td className={toneOf(row.totalR)}>{formatR(row.totalR)}</td>
                    <td className={toneOf(row.expectancyR)}>{formatR(row.expectancyR)}</td>
                    <td>{formatPercent(row.winRate)}</td>
                    <td>{row.instruments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="tbl-foot">
            <span>
              {rows.length > ROWS_SHOWN
                ? `${recent.length} de ${rows.length} ${noun === "semana" ? "semanas" : "meses"}, los mas recientes`
                : `${rows.length} ${rows.length === 1 ? noun : noun === "semana" ? "semanas" : "meses"}`}
            </span>
            <span>
              <Icon name="trend" /> Solo operaciones cerradas
            </span>
          </div>
        </StatusBlock>
      </div>
    </section>
  );
}

/**
 * La clave del servidor, en palabras: "2026-W38" es "Semana 38 de 2026" y
 * "2026-09" es "Septiembre de 2026". Si la clave no tiene la forma esperada se
 * ensena tal cual: mejor una clave cruda que una fecha inventada.
 */
export function labelPeriod(key: string, granularity: PeriodGranularity): string {
  if (granularity === "week") {
    const match = /^(\d{4})-W(\d{2})$/.exec(key);

    if (match === null) return key;

    return `Semana ${Number(match[2])} de ${match[1]}`;
  }

  const match = /^(\d{4})-(\d{2})$/.exec(key);

  if (match === null) return key;

  return formatMonth(Number(match[1]), Number(match[2]));
}

function readGranularity(): PeriodGranularity {
  try {
    return window.localStorage.getItem(GRANULARITY_KEY) === "month" ? "month" : "week";
  } catch {
    return "week";
  }
}

export type { PeriodRow };
